import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permissionLevelEnum } from '../../../../constants';
import { useCreateOrUpdateAbsentsReportsMutation } from '../../../../hooks/mutations/useReportMutation';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { Report } from '../../../../services/apiSLTP/dailyReports/reports';
import { formatName } from '../../../../utils/capitalize';
import CustomDateInput from '../../../ui/CustomDateInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody from '../../ModalBody';

interface ModalProps {
    open: boolean;
    handleClose: () => void;
    refetch?: () => void;
    initUserId?: string;
    currentDate: Date;
    reports: Report[];
}

const CreateAbsentsReportsModal: React.FC<ModalProps> = ({ open, handleClose, refetch = () => {}, initUserId = '', currentDate, reports }) => {
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();

    const { t } = useTranslation();

    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });

    const { mutate: createReports, isLoading: isCreateReportsLoading } = useCreateOrUpdateAbsentsReportsMutation({
        onSuccess: (data) => {
            refetch();
            handleClose();
        },
    });

    useEffect(() => {
        if (initUserId.length) {
            const tmpUser = dataUsers?.find((user) => user._id === initUserId && !user.isSpecial);
            setSelectedUser({
                label: tmpUser ? formatName(tmpUser.firstName, tmpUser.lastName) : '',
                value: initUserId,
            });
        }
    }, []);

    return (
        <ModalBody
            title={t('absences')}
            subtitle={t('add')}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                if (startDate && endDate && selectedUser)
                    createReports({ body: { userId: selectedUser?.value, startDate: startDate.toISOString(), endDate: endDate.toISOString() } });
            }}
            disableSubmit={!(selectedUser?.value.length && startDate && endDate)}
            submitLoading={isCreateReportsLoading}
        >
            <DropDown
                selectedItem={selectedUser}
                items={
                    dataUsers
                        ?.filter((user) => !user.isSpecial && user.profileId.permsLevel >= permissionLevelEnum.TERRAIN)
                        ?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                }
                handleChange={(event, value) => setSelectedUser(value)}
                inputLabel={t('person')}
                loading={isUsersLoading}
                required
            />
            <CustomDateInput
                label={t('startDate')}
                onChange={(value) => (value ? setStartDate(new Date(value?.toISOString())) : null)}
                defaultDate={startDate}
                required
                minDate={
                    dataUsers?.find((user) => user._id === selectedUser?.value)?.arrivalDate
                        ? new Date(dataUsers?.find((user) => user._id === selectedUser?.value)?.arrivalDate || '')
                        : undefined
                }
                maxDate={endDate}
            />
            <CustomDateInput
                label={t('endDate')}
                onChange={(value) => (value ? setEndDate(new Date(value?.toISOString())) : null)}
                defaultDate={endDate}
                required
                minDate={startDate}
                maxDate={
                    dataUsers?.find((user) => user._id === selectedUser?.value)?.departureDate
                        ? new Date(dataUsers?.find((user) => user._id === selectedUser?.value)?.departureDate || '')
                        : undefined
                }
            />
        </ModalBody>
    );
};

export default CreateAbsentsReportsModal;
